import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  DataGrid,
  GridToolbar,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridToolbarDensitySelector,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import { CSVLink } from 'react-csv';
import { FileText, Settings } from 'lucide-react';
import { 
  Box, 
  TextField, 
  Typography,
  ThemeProvider,
  createTheme,
  Popover,
  FormControlLabel,
  Checkbox,
  FormGroup
} from '@mui/material';

// Button component
const Button = ({ children, ...props }) => (
  <button {...props} className="px-4 py-2 bg-red-800 text-white rounded hover:bg-red-800 transition-colors">
    {children}
  </button>
);

function CustomToolbar() {
  return (
    <GridToolbarContainer className="flex justify-between p-2 bg-white">
      <div className="flex gap-2">
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
      </div>
    </GridToolbarContainer>
  );
}

function CustomPagination() {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const rowCount = apiRef.current.getRowsCount();
  const pageSize = apiRef.current.getState().pagination.paginationModel.pageSize;
  
  // Calculate the current range
  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, rowCount);

  return (
    <div className="flex justify-end items-center w-full px-4 py-2">
      <div className="flex items-center">
        <div className="mr-4 flex items-center">
          <span className="mr-2 text-sm text-gray-600">Page Size:</span>
          <div className="relative">
            <select 
              className="appearance-none bg-white border border-gray-300 rounded px-3 py-1 pr-8 focus:outline-none"
              value={pageSize}
              onChange={(e) => apiRef.current.setPageSize(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
              </svg>
            </div>
          </div>
        </div>

        <span className="mx-4 text-sm text-gray-600">
          {`${startRow} to ${endRow} of ${rowCount}`}
        </span>
        
        <button
          onClick={() => apiRef.current.setPage(0)}
          disabled={page === 0}
          className="p-1 mx-1 text-gray-600 disabled:text-gray-300"
          aria-label="First page"
        >
          ⟪
        </button>
        
        <button
          onClick={() => apiRef.current.setPage(page - 1)}
          disabled={page === 0}
          className="p-1 mx-1 text-gray-600 disabled:text-gray-300"
          aria-label="Previous page"
        >
          ⟨
        </button>
        
        <span className="mx-2 text-sm text-gray-600">
          {`Page ${page + 1} of ${Math.max(1, pageCount)}`}
        </span>
        
        <button
          onClick={() => apiRef.current.setPage(page + 1)}
          disabled={page >= pageCount - 1}
          className="p-1 mx-1 text-gray-600 disabled:text-gray-300"
          aria-label="Next page"
        >
          ⟩
        </button>
        
        <button
          onClick={() => apiRef.current.setPage(pageCount - 1)}
          disabled={page >= pageCount - 1}
          className="p-1 mx-1 text-gray-600 disabled:text-gray-300"
          aria-label="Last page"
        >
          ⟫
        </button>
      </div>
    </div>
  );
}

// Create a custom theme matching the image
const customTheme = createTheme({
  palette: {
    primary: {
      main: '#991b1b',
      light: '#b91c1c',
      dark: '#7f1d1d',
    },
  },
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#991b1b',
            color: '#ffffff',
            fontWeight: 700,
            borderBottom: 'none',
            minHeight: '120px !important',
            maxHeight: '120px !important',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 500,
            fontSize: '0.875rem',
            overflow: 'visible',
            lineHeight: '1.2',
            whiteSpace: 'normal',
            textOverflow: 'clip',
            textTransform: 'none',
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: '1px solid rgba(255, 255, 255, 0.2)',
            whiteSpace: 'normal',
            lineHeight: '1.2',
            height: '120px !important',
            overflow: 'visible',
          },
          '& .MuiDataGrid-columnHeader:hover': {
            overflow: 'visible',
            zIndex: 5,
          },
          '& .MuiDataGrid-columnHeaderTitleContainer': {
            padding: '0 8px',
            justifyContent: 'flex-start',
            alignItems: 'center',
          },
          '& .MuiDataGrid-columnHeaderCheckbox': {
            backgroundColor: '#991b1b',
            borderRight: 'none',
          },
          '& .MuiDataGrid-row': {
            borderBottom: 'none',
            minHeight: '36px !important',
            maxHeight: '40px !important',
            backgroundColor: '#ffffff',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#e6f7ff !important',
            cursor: 'pointer !important',
          },
          '& .Mui-hovered': {
            backgroundColor: '#e6f7ff !important',
          },
          '& .MuiDataGrid-cell': {
            borderRight: 'none',
            borderBottom: '1px solid #e5e7eb',
            fontSize: '0.875rem',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            padding: '20px 16px',
            height: '36px',
            backgroundColor: '#ffffff',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: '#ffffff',
            padding: '8px 0',
          },
          '& .MuiTablePagination-root': {
            color: '#4b5563',
          },
          '& .MuiDataGrid-cellCheckbox': {
            backgroundColor: '#ffffff',
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-iconSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: '#ffffff',
          },
          '& .MuiDataGrid-main': {
            backgroundColor: '#ffffff',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#d1d5db',
          '&.Mui-checked': {
            color: '#991b1b',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          color: '#991b1b',
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#991b1b',
            color: '#ffffff',
          },
        },
      },
    },
  },
});

const AdvanceMaterialTable = ({ 
  data = [], 
  defaultVisibleColumns = ['composition', 'DOI', 'morphology', 'reaction', 'onset_potential_(V vs. RHE at +/-100_μA/cm2)', 'onset_potential_(V vs. RHE at +/-1000_μA/cm2)', 'pH', 'repilcate', 'onset_potential_mean_std(+/-0.1_mA/cm2)'], 
  onSelectedRowsChange = () => {}}) => {
  
  const [searchText, setSearchText] = useState('');
  const [selectionModel, setSelectionModel] = useState([]);
  const [pageSize, setPageSize] = useState(15);
  const [anchorEl, setAnchorEl] = useState(null);

  const initialVisibilityModel = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return {};
    
    const allColumns = Object.keys(data[0]);
    const columnsToShow = ['SN', ...defaultVisibleColumns];
    
    return allColumns.reduce((acc, column) => {
      const shouldBeVisible = columnsToShow.some(col => 
        col.toLowerCase() === column.toLowerCase()
      );
      acc[column] = shouldBeVisible;
      return acc;
    }, {});
  }, [data, defaultVisibleColumns]);
  
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => initialVisibilityModel);

  useEffect(() => {
    if (Object.keys(columnVisibilityModel).length === 0) {
      setColumnVisibilityModel(initialVisibilityModel);
    }
  }, [initialVisibilityModel]);

  const rows = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((item, index) => ({
      ...item,
      id: index,
      SN: index + 1
    }));
  }, [data]);

  const getSelectedRowsData = useCallback(() => {
    if (!selectionModel.length) return rows;
    const selectedData = selectionModel.map(id => {
      const row = rows.find(r => r.id === id);
      return row ? row : null;
    }).filter(Boolean);
    return selectedData;
  }, [selectionModel, rows]);

  const shouldDisplayAsDash = (value) => {
    if (value === null || value === undefined) return true;
    if (value === "0") return true;
    if (typeof value === 'string' && (value === "0.00" || value === "0.0" || value.trim() === "")) return true;
    return false;
  };

  const formatColumnHeader = (headerName) => {
    const lowerHeader = headerName.toLowerCase();
    
    if (lowerHeader === 'doi') {
      return (
        <div className="flex justify-start items-center text-left w-full h-full"
             title="DOI">
          DOI
        </div>
      );
    } else if (lowerHeader === 'ph') {
      return (
        <div className="flex justify-start items-center text-left w-full h-full"
             title="pH">
          pH
        </div>
      );
    } else if (lowerHeader === 'sn') {
      return (
        <div className="flex justify-start items-center text-left w-full h-full"
             title="SN">
          SN
        </div>
      );
    }
    
    if (lowerHeader.includes('onset potential') && headerName.includes('(')) {
      const parts = headerName.split('(');
      const baseName = parts[0].trim();
      const capitalized = baseName.charAt(0).toUpperCase() + baseName.slice(1).toLowerCase();
      const valueInfo = parts[1].replace(')', '');
      const fullTitle = `${capitalized} (V vs RHE at ${valueInfo})`;
      
      return (
        <div className="flex flex-col justify-start items-start text-left w-full h-full" 
             title={fullTitle}>
          <div>{capitalized}</div>
          <div>(V vs. RHE at</div>
          <div>{valueInfo})</div>
        </div>
      );
    }
    
    const capitalized = headerName.charAt(0).toUpperCase() + headerName.slice(1).toLowerCase();
    return (
      <div className="flex justify-start items-center text-left w-full h-full"
           title={capitalized}>
        {capitalized}
      </div>
    );
  };

  const columns = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];
    const firstRow = data[0];
    if (!firstRow) return [];

    const snColumn = {
      field: 'SN',
      headerName: 'SN',
      width: 70,
      sortable: false,
      filterable: false,
      headerClassName: 'super-app-theme--header',
      headerAlign: 'left',
      align: 'left',
      hide: false,
      renderHeader: () => formatColumnHeader('SN'),
    };

    const dataColumns = Object.keys(firstRow).map(key => {
      let headerName;
      
      if (key.toLowerCase() === 'doi') {
        headerName = 'DOI';
      } else if (key.toLowerCase() === 'ph') {
        headerName = 'PH';
      } else if (key.toLowerCase() === 'sn') {
        headerName = 'SN';
      } else {
        headerName = key.charAt(0).toUpperCase() + 
                key.slice(1).replace(/_/g, ' ')
                   .replace(/\(\+\-0\.1_mA\/cm2\)/g, "(±0.1 mA/cm²)");
      }
      
      const isOnsetPotential = key.toLowerCase().includes('onset_potential');
      
      return {
        field: key,
        headerName: headerName,
        flex: 1,
        minWidth: 130,
        sortable: true,
        filterable: true,
        headerClassName: 'super-app-theme--header',
        headerAlign: 'left',
        align: 'left',
        renderHeader: (params) => formatColumnHeader(params.colDef.headerName),
        
        ...(isOnsetPotential && {
          renderCell: (params) => {
            if (shouldDisplayAsDash(params.value)) {
              return '-';
            }
            return params.value;
          }
        }),
        
        ...(key.toLowerCase() === 'doi' && {
          minWidth: 220,
          width: 220,
          renderCell: (params) => {
            if (!params.value) return '';
            return (
              <div className="w-full flex justify-start items-center">
                <a
                  href={`https://dx.doi.org/${params.value}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-xs font-medium rounded-md overflow-hidden"
                >
                  <span className="bg-gray-800 text-white px-2 font-bold py-1">DOI</span>
                  <span className="bg-blue-100 text-blue-800 px-2 font-bold py-1 hover:underline">
                    {params.value}
                  </span>
                </a>
              </div>
            );
          }
        })
      };
    });

    return [snColumn, ...dataColumns];
  }, [data]);

  const handleSelectionModelChange = (newSelectionModel) => {
    setSelectionModel(newSelectionModel);
    onSelectedRowsChange(newSelectionModel);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
  };

  // Handle Manage Columns popover
  const handleManageColumnsClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleManageColumnsClose = () => {
    setAnchorEl(null);
  };

  const handleColumnVisibilityChange = (field) => {
    setColumnVisibilityModel((prev) => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const open = Boolean(anchorEl);

  if (!Array.isArray(data)) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="error">
          No data available or invalid data format
        </Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={customTheme}>
      <div className="w-full h-full flex flex-col gap-3" style={{ backgroundColor: '#ffffff' }}>
        <div className="flex justify-between items-center">
          <Typography variant="h6" className="font-semibold">
            {data.length} materials match your search
            {selectionModel.length > 0 && ` (${selectionModel.length} selected)`}
          </Typography>
          
          <div className="flex items-center space-x-4">
            <TextField
              value={searchText}
              onChange={handleSearchChange}
              placeholder="Search across all columns..."
              variant="outlined"
              size="small"
              className="min-w-[250px]"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  '& fieldset': {
                    borderColor: '#e5e7eb',
                  },
                  '&:hover fieldset': {
                    borderColor: '#d1d5db',
                  },
                }
              }}
            />
            
            <Button onClick={handleManageColumnsClick} className="bg-red-800 hover:bg-red-900">
              <Settings className="inline-block mr-2" size={16} />
              Manage Columns
            </Button>
            
            <CSVLink
              data={getSelectedRowsData()}
              filename="MaterialsData.csv"
              className="no-underline"
            >
              <Button className="bg-red-800 hover:bg-red-900">
                <FileText className="inline-block mr-2" size={16} />
                Export Table
              </Button>
            </CSVLink>
          </div>
        </div>

        {/* Manage Columns Popover */}
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleManageColumnsClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box sx={{ p: 2, maxHeight: 400, overflowY: 'auto', minWidth: 250 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Show/Hide Columns
            </Typography>
            <FormGroup>
              {columns.map((col) => (
                <FormControlLabel
                  key={col.field}
                  control={
                    <Checkbox
                      checked={columnVisibilityModel[col.field] !== false}
                      onChange={() => handleColumnVisibilityChange(col.field)}
                      size="small"
                    />
                  }
                  label={col.headerName}
                />
              ))}
            </FormGroup>
          </Box>
        </Popover>

        <Box sx={{ 
          height: 600, 
          width: '100%',
          backgroundColor: '#ffffff',
          '& .MuiDataGrid-root': {
            border: 'none',
            backgroundColor: '#ffffff',
          },
          '& .super-app-theme--header': {
            backgroundColor: '#991b1b',
            color: '#ffffff',
          },
        }}>
          <DataGrid
            rows={rows}
            columns={columns}
            autoWidth={false}
            pageSizeOptions={[10, 15, 20, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 15, page: 0 },
              },
              sorting: {
                sortModel: [{ field: 'id', sort: 'asc' }],
              },
            }}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={handleSelectionModelChange}
            components={{
              Toolbar: CustomToolbar,
              Pagination: CustomPagination,
            }}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            getRowId={(row) => row.id}
            filterMode="client"
            quickFilterValues={[searchText]}
            density="compact"
            rowHeight={36}
            headerHeight={120}
            sx={{
              boxShadow: 2,
              backgroundColor: '#ffffff',
              '& .MuiDataGrid-cell:hover': {
                color: 'primary.main',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#e6f7ff !important',
                cursor: 'pointer',
              },
              '& .Mui-hovered': {
                backgroundColor: '#e6f7ff !important',
              },
              '& .MuiDataGrid-row:nth-of-type(odd)': {
                backgroundColor: '#ffffff',
              },
              '& .MuiDataGrid-row:nth-of-type(even)': {
                backgroundColor: '#ffffff',
              },
              '& .MuiDataGrid-virtualScroller': {
                backgroundColor: '#ffffff',
              },
              '& .MuiDataGrid-main': {
                backgroundColor: '#ffffff',
              },
            }}
          />
        </Box>
      </div>
    </ThemeProvider>
  );
};

export default AdvanceMaterialTable;